import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { SubmittalJobEmailDto } from 'src/app/accountmanagers/submittals/am-submittal-status-update/model/am-submittal-job-email.model';
import { SubmittalJobStatusData } from 'src/app/accountmanagers/submittals/am-submittal-status-update/model/am-submittal-job-status-data.model';
import {
    AmSubmittalSendToClientFile,
    AmSubmittalSendToClientFileInfo
} from 'src/app/accountmanagers/submittals/am-submittal-status-update/model/am-submittal-send-to-client-file.model';
import { SubmittalSendToClientUpdateResult } from 'src/app/accountmanagers/submittals/am-submittal-status-update/model/am-submittal-send-to-client-update-result.model';
import { AmSubmittalStatuses } from 'src/app/accountmanagers/submittals/am-submittal-status/model/am-submittal-statuses.enum';
import { APNAmSubmittalStatuses } from 'src/app/accountmanagers/submittals/am-submittal-status/model/apn-am-submittal-statuses.enum';
import { AMNoteItem, AMSubmittalJob } from 'src/app/accountmanagers/submittals/model/am-submittals.model';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { JobPreferences } from 'src/app/shared/components/job-preferences/job-preferences-model';
import { PersonalProfessionalInfo } from 'src/app/shared/components/personal-professional-info/models/personal-professional-info.model';
import { IdNameModel } from 'src/app/shared/models/id-name.model';
import { combine } from 'src/app/shared/utilities/route';
import { SubmittalTypeEnum } from '../model/am-submittals.enum';
import { SubmittalStatusEnum } from '../model/enums/submittal-status.enum';
import { lineOfBusinessWithoutOfferWorkflowForLink } from 'src/app/shared/models/enums/employment-type-code.enum';
import { GetApplyWithAyaSubmittalAdditionalInfoResponse } from './send-to-client-modal/advanced-send-to-client-portal/models/application-question-answers.model';

@Injectable({
    providedIn: 'root'
})
export class AmSubmittalStatusUpdateService implements OnDestroy {
    private readonly coreApiPath: string;

    private readonly statusHasBeenUpdatedMessage = 'Job submittal status has been updated';
    private readonly jobHasBeenRemovedMessage = 'Job submittal(s) have been removed from your list';

    private readonly _submittalJobChanged: EventEmitter<{
        submittalJob: AMSubmittalJob;
        removeFromList: boolean;
        doOpenOfferLink: boolean;
    }> = new EventEmitter<{ submittalJob: AMSubmittalJob; removeFromList: boolean; doOpenOfferLink: boolean }>();

    private readonly _submittalChanged: EventEmitter<{ submittalId: number; removeFromList: boolean }> =
        new EventEmitter<{ submittalId: number; removeFromList: boolean }>();

    private readonly _noteChanged: EventEmitter<AMNoteItem> = new EventEmitter<AMNoteItem>();

    private readonly _apnNoteChanged: EventEmitter<AMNoteItem> = new EventEmitter<AMNoteItem>();

    private readonly _removedAndUpdatedMessage = 'Job submittal(s) have been updated & removed from your list';

    get submittalJobChanged(): Observable<{
        submittalJob: AMSubmittalJob;
        removeFromList: boolean;
        doOpenOfferLink: boolean;
    }> {
        return this._submittalJobChanged.asObservable();
    }

    get submittalChanged(): Observable<{ submittalId: number; removeFromList: boolean }> {
        return this._submittalChanged.asObservable();
    }

    get noteChanged(): Observable<AMNoteItem> {
        return this._noteChanged.asObservable();
    }

    get apnNoteChanged(): Observable<AMNoteItem> {
        return this._apnNoteChanged.asObservable();
    }

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) settings: Settings,
        private readonly _store: Store
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    ngOnDestroy(): void {
        this._submittalJobChanged.complete();
        this._submittalChanged.complete();
        this._noteChanged.complete();
    }

    apnSendToClientPortal(submittalId: number, jobId: number, request: SubmittalJobStatusData): Observable<boolean> {
        return this.setStatus(submittalId, jobId, request, () => 'Job submittal has been sent to client portal');
    }

    placed(submittalId: number, jobId: number, request: SubmittalJobStatusData): Observable<boolean> {
        return this.setStatus(submittalId, jobId, request, () => this._removedAndUpdatedMessage);
    }

    apnInactivate(submittalId: number, jobId: number, request: SubmittalJobStatusData): Observable<boolean> {
        return this.setStatus(submittalId, jobId, request, () => 'Job submittal(s) have been inactivated');
    }

    setStatusSentToClient(submittalId: number, jobId: number, request: SubmittalJobStatusData) {
        return this.setStatus(submittalId, jobId, request, () => this.statusHasBeenUpdatedMessage);
    }

    sendToClientPortal(submittalId: number, jobId: number, request: SubmittalJobStatusData): Observable<boolean> {
        return this.setStatus(submittalId, jobId, request, () => 'Job submittal has been sent to client portal');
    }

    setStatusSentToClinical(submittalId: number, jobId: number, request: SubmittalJobStatusData) {
        return this.setStatus(submittalId, jobId, request, () => this.statusHasBeenUpdatedMessage);
    }

    inactivate(submittalId: number, jobId: number, request: SubmittalJobStatusData): Observable<boolean> {
        return this.setStatus(submittalId, jobId, request, (x) =>
            x.removeFromList ? this.jobHasBeenRemovedMessage : 'Job submittal(s) have been inactivated'
        );
    }

    updateNewStatus(submittalId: number, jobId: number, request: SubmittalJobStatusData): Observable<boolean> {
        return this.setStatus(submittalId, jobId, request, (r) =>
            r.removeFromList ? this.jobHasBeenRemovedMessage : this.statusHasBeenUpdatedMessage
        );
    }

    pendingAMReviewOrSubmittedToAya(
        submittalId: number,
        jobId: number,
        request: SubmittalJobStatusData
    ): Observable<boolean> {
        return this.setStatus(submittalId, jobId, request, () => this.statusHasBeenUpdatedMessage);
    }

    nameClear(submittalId: number, jobId: number, request: SubmittalJobStatusData): Observable<boolean> {
        return this.setStatus(submittalId, jobId, request, () => this.statusHasBeenUpdatedMessage);
    }

    triggerSubmittalStatusChanged(submittalId: number, removeFromList: boolean) {
        this._submittalChanged.next({
            submittalId: submittalId,
            removeFromList: removeFromList
        });
    }

    /**
     * Updates the status of a submittal job and handles the response.
     *
     * @param submittalId - The ID of the submittal.
     * @param jobId - The ID of the job.
     * @param request - The data required to update the submittal job status.
     * @param successMessage - A function that generates a success message based on the request and response.
     * @param failureMessage - A message to display if the status update fails. Defaults to 'Status update failed, please try again later'.
     * @returns An observable that emits a boolean indicating the success of the operation.
     */
    setStatus(
        submittalId: number,
        jobId: number,
        request: SubmittalJobStatusData,
        successMessage: (
            SubmittalJobStatusData: SubmittalJobStatusData,
            SubmittalSendToClientUpdateResult: SubmittalSendToClientUpdateResult
        ) => string,
        failureMessage = 'Status update failed, please try again later'
    ): Observable<boolean> {
        const result$ = new Subject<boolean>();

        const body = request;
        this._httpClient
            .post<SubmittalSendToClientUpdateResult>(
                `${this.coreApiPath}/accountManager/submittals/${submittalId}/jobs/${jobId}/status`,
                body
            )
            .subscribe({
                next: (e) => {
                    this._store.dispatch(showToasterMessage({ message: successMessage(request, e), duration: 5000 }));

                    if (e.submittalJob) {
                        let openOfferLink = e.submittalJob.submittalStatusId === SubmittalStatusEnum.Offer.valueOf();

                        if (openOfferLink) {
                            openOfferLink = !lineOfBusinessWithoutOfferWorkflowForLink.includes(
                                e.submittalJob.employmentTypeCode
                            );
                        }

                        this._submittalJobChanged.next({
                            submittalJob: e.submittalJob,
                            removeFromList: e.removedFromList,
                            doOpenOfferLink: openOfferLink
                        });
                    }

                    this.triggerSubmittalStatusChanged(submittalId, e.removedSubmittalFromList);

                    if (e.submittalNote) {
                        // some of the AYA and APN status changes are wired to the wrong method
                        // so triggering both listeners though only the appropriate one
                        // will be able to act due to the submittal ID
                        this._noteChanged.next(e.submittalNote);
                        this._apnNoteChanged.next(e.submittalNote);
                    }

                    result$.next(true);
                },
                error: (_error) => {
                    return this.handleError(failureMessage, result$);
                }
            });

        return result$.asObservable();
    }

    notifyNoteUpdated(note: AMNoteItem) {
        this._noteChanged.next(note);
        this._apnNoteChanged.next(note);
    }

    notifySubmittalJobUpdated(submittalJob: AMSubmittalJob, removeFromList: boolean, doOpenOfferLink: boolean) {
        this._submittalJobChanged.next({ submittalJob, removeFromList, doOpenOfferLink });
    }

    handleError(failureMessage: string, subject$: Subject<boolean>) {
        this._store.dispatch(showToasterMessage({ message: failureMessage, duration: 5000 }));
        subject$.next(false);
    }

    getRecipients(facilityId: number): Observable<SubmittalJobEmailDto[]> {
        const url = `${this.coreApiPath}/accountManager/submittals/contacts/${facilityId}`;

        return this._httpClient.get<SubmittalJobEmailDto[]>(url);
    }

    getJobPreferences(candidateId: number): Observable<JobPreferences> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/job-preferences`;

        return this._httpClient.get<JobPreferences>(url);
    }

    getContactInfo(candidateId: number, isSubcontractor = false): Observable<PersonalProfessionalInfo> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/profile?isSubcontractor=${isSubcontractor}`;

        return this._httpClient.get<PersonalProfessionalInfo>(url);
    }

    getApplyWithAyaSubmittalQuestionAnswers(
        submittalJobId: number
    ): Observable<GetApplyWithAyaSubmittalAdditionalInfoResponse> {
        const url = `${this.coreApiPath}/PermanentJobSubmittal/${submittalJobId}/details`;

        return this._httpClient.get<GetApplyWithAyaSubmittalAdditionalInfoResponse>(url);
    }

    getInactiveReasons(
        submittalType: SubmittalTypeEnum,
        status: AmSubmittalStatuses | APNAmSubmittalStatuses
    ): Observable<IdNameModel<number, string>[]> {
        const url = `${this.coreApiPath}/accountManager/submittals/status/inactive-reasons?submittalType=${submittalType}&status=${status}`;

        return this._httpClient.get<IdNameModel<number, string>[]>(url);
    }

    attachFile(
        file: File,
        submittalId: number,
        jobId: number,
        submittalType: SubmittalTypeEnum
    ): Observable<AmSubmittalSendToClientFile> {
        const url = `${this.coreApiPath}/accountManager/submittals/${submittalId}/jobs/${jobId}/status/attachment?submittalType=${submittalType}`;
        const data = new FormData();

        data.append('file', file, file.name);

        return this._httpClient.post<AmSubmittalSendToClientFile>(url, data);
    }

    attachFiles(
        files: File[],
        submittalId: number,
        jobId: number,
        submittalType: SubmittalTypeEnum
    ): Observable<AmSubmittalSendToClientFileInfo[]> {
        const url = `${this.coreApiPath}/accountManager/submittals/${submittalId}/jobs/${jobId}/status/attachments?submittalType=${submittalType}`;
        const data = new FormData();

        files.forEach((document) => {
            data.append('file', document, document.name);
        });

        return this._httpClient.post<AmSubmittalSendToClientFileInfo[]>(url, data);
    }

    deleteFile(
        fileUrl: string,
        submittalId: number,
        jobId: number,
        submittalType: SubmittalTypeEnum
    ): Observable<void> {
        const url = `${this.coreApiPath}/accountManager/submittals/${submittalId}/jobs/${jobId}/status/attachment?submittalType=${submittalType}`;

        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: {
                fileUrl
            }
        };

        return this._httpClient.delete<void>(url, options);
    }

    emitApnNoteUpdated(note: AMNoteItem) {
        this._apnNoteChanged.next(note);
    }
}
